import {
  createEssayFn,
  deleteEssayFn,
  getEssayByIdFn,
  listAllEssaysOptions,
  togglePublishEssayFn,
  updateEssayFn,
} from "@/actions/essay/handler";
import type { SelectEssay } from "@/api/db/schema/essay";
import { EssayForm } from "@/components/admin/essayForm";
import { EssayList, type EssayMetadata } from "@/components/admin/essayList";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import type { SerializedEditorState } from "lexical";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/essay")({
  beforeLoad: async ({ context }) => {
    if (
      !context.user ||
      (context.user.role !== "admin" && context.user.role !== "author")
    ) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listAllEssaysOptions());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const [activeTab, setActiveTab] = useState<"list" | "create">("list");
  const [editingEssay, setEditingEssay] = useState<SelectEssay | null>(null);

  const essaysQuery = useSuspenseQuery(listAllEssaysOptions());
  const essays = (essaysQuery.data.body?.essays || []) as EssayMetadata[];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createEssayFn,
    onSuccess: () => {
      toast.success("Essay başarıyla oluşturuldu!");
      essaysQuery.refetch();
      setActiveTab("list");
    },
    onError: (error) => {
      toast.error(error.message || "Essay oluşturulurken bir hata oluştu");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: updateEssayFn,
    onSuccess: () => {
      toast.success("Essay başarıyla güncellendi!");
      essaysQuery.refetch();
      setEditingEssay(null);
      setActiveTab("list");
    },
    onError: (error) => {
      toast.error(error.message || "Essay güncellenirken bir hata oluştu");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteEssayFn,
    onSuccess: () => {
      toast.success("Essay başarıyla silindi!");
      essaysQuery.refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Essay silinirken bir hata oluştu");
    },
  });

  // Toggle publish mutation
  const togglePublishMutation = useMutation({
    mutationFn: togglePublishEssayFn,
    onSuccess: () => {
      toast.success("Essay durumu güncellendi!");
      essaysQuery.refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Durum güncellenirken bir hata oluştu");
    },
  });

  // Fetch full essay mutation (for editing)
  const fetchEssayMutation = useMutation({
    mutationFn: getEssayByIdFn,
    onError: (error) => {
      toast.error(error.message || "Essay içeriği yüklenirken bir hata oluştu");
    },
  });

  const handleCreate = async (data: {
    title: string;
    content: SerializedEditorState;
    coverImageUrl?: string | null;
    metaDescription?: string | null;
    ogImageUrl?: string | null;
    isPublished?: boolean;
  }) => {
    await createMutation.mutateAsync({
      data,
    });
  };

  const handleUpdate = async (data: {
    title: string;
    content: SerializedEditorState;
    coverImageUrl?: string | null;
    metaDescription?: string | null;
    ogImageUrl?: string | null;
    isPublished?: boolean;
  }) => {
    if (!editingEssay) return;
    await updateMutation.mutateAsync({
      data: {
        essayId: editingEssay.essayId,
        ...data,
      },
    });
  };

  const handleEdit = async (essay: EssayMetadata) => {
    try {
      toast.loading("Essay içeriği yükleniyor...");
      const fullEssayResponse = await fetchEssayMutation.mutateAsync({
        data: { essayId: essay.essayId },
      });

      if (fullEssayResponse.status === 200 && fullEssayResponse.body) {
        setEditingEssay(fullEssayResponse.body as SelectEssay);
        setActiveTab("create");
        toast.dismiss();
      } else {
        toast.error("Essay içeriği yüklenemedi");
      }
    } catch (_error) {
      // Toast error is handled in mutation onError
      toast.dismiss();
    }
  };

  const handleCancelEdit = () => {
    setEditingEssay(null);
    setActiveTab("list");
  };

  return (
    <div className="container mx-auto px-4 py-24 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="mb-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />← Admin Paneli
            </Link>
          </Button>
        </div>
        <h1 className="mb-2 font-bold text-2xl sm:text-3xl">Essay Yönetimi</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Essay'leri oluşturun, düzenleyin ve yayınlayın
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full max-w-[400px] grid-cols-2">
          <TabsTrigger value="list">Tüm Essayler ({essays.length})</TabsTrigger>
          <TabsTrigger value="create">
            {editingEssay ? "Düzenle" : "Yeni Oluştur"}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6">
          <EssayList
            essays={essays}
            onEdit={handleEdit}
            onDelete={(id) =>
              deleteMutation.mutate({
                data: { essayId: id },
              })
            }
            onTogglePublish={(id) =>
              togglePublishMutation.mutate({
                data: { essayId: id },
              })
            }
            isLoading={
              deleteMutation.isPending || togglePublishMutation.isPending
            }
          />
        </TabsContent>

        <TabsContent value="create" className="mt-6">
          <EssayForm
            initialData={editingEssay ?? undefined}
            onSubmit={editingEssay ? handleUpdate : handleCreate}
            onCancel={editingEssay ? handleCancelEdit : undefined}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
